import { createWebHistory, createRouter } from "vue-router";
import { getCurrentUser, signOut } from "aws-amplify/auth";
import LandingPage from "@/screens/landing/LandingPage.vue";
import DashboardIndexPage from "@/utility/DashboardIndexPage.vue";
import RiskProfilePage from "@/screens/landing/RiskProfilePage.vue";
import FinancialCalculatorsPage from "@/screens/landing/FinancialCalculatorsPage.vue";
import { useUserStore } from "@/store/user-store";
import AddHoldingsWelcome from '@/sections/dashboard/AddHoldingsWelcome.vue'
import SEBIDisclosures from "@/screens/landing/SEBIDisclosures.vue";

const routes = [
  {
    path: "/",
    name: "Landing",
    component: LandingPage,
    meta: { redirectIfAuth: true },
  },
  {
    path: "/oauth/kite",
    name: "KiteOAuth",
    component: () => import("@/components/oauth/KiteOAuthHandler.vue"),
    meta: { requiresAuth: false, isOAuthCallback: true }
  },
  {
    path: "/risk-assessment",
    name: "RiskAssessment",
    component: RiskProfilePage,
    meta: {
      requiresAuth: false,
      isDemoMode: true // Educational/demo mode for public users
    }
  },
  {
    path: "/financial-calculators",
    name: "FinancialCalculators",
    component: FinancialCalculatorsPage,
    meta: { requiresAuth: false },
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: DashboardIndexPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/dashboard/model-portfolio",
    name: "ModelPortfolio",
    component: () => import("@/tabs/user-dashboard/MyModelPortfolio.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: '/sector-analytics-detail',
    name: 'SectorAnalyticsDetail',
    component: () => import('@/tabs/sector-analytics/SectorAnalyticsDetail.vue'),
    meta: {
      requiresAuth: true,
      title: 'Sector Analytics Detail'
    }
  },
  {
    path: '/dashboard/add-holdings',
    name: 'AddHoldings',
    component: () => import('@/sections/dashboard/AddHoldingsWelcome.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/sebi-disclosures',
    name: 'SEBIDisclosures',
    component: SEBIDisclosures,
    meta: { requiresAuth: false }
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (user used back/forward), use it
    if (savedPosition) {
      return savedPosition;
    }
    // Otherwise, scroll to top
    return { top: 0, behavior: 'smooth' };
  }
});

router.beforeEach(async (to, from, next) => {
  try {
    // CRITICAL: Check for Kite OAuth callback BEFORE any other logic
    const urlParams = new URLSearchParams(window.location.search);
    const requestToken = urlParams.get('request_token');
    const isPopupWindow = window.opener && window.opener !== window;

    console.log('🔍 [Router Guard] Checking for OAuth callback:', {
      requestToken: requestToken ? 'EXISTS' : 'NONE',
      isPopupWindow,
      pathname: to.path,
      fullPath: to.fullPath,
      query: to.query
    });

    // If this is a Kite OAuth callback in a popup, redirect to OAuth handler route
    if (requestToken && isPopupWindow && to.path === '/') {
      console.log('✅ [Router Guard] Kite OAuth callback detected - redirecting to /oauth/kite');
      return next({
        path: '/oauth/kite',
        query: { ...to.query } // Preserve query parameters
      });
    }

    // If already on OAuth route, allow through
    if (to.meta.isOAuthCallback) {
      console.log('✅ [Router Guard] On OAuth callback route - allowing through');
      return next();
    }

    const store = useUserStore();
    const user = await getCurrentUser();
    const isAuthenticated = !!user;

    // Reset body overflow on route changes to prevent scroll issues
    document.body.style.overflow = "";

    // Check if store has user data loaded (critical for actual app functionality)
    const hasStoreData = !!store.getuserData;

    // NEW: Check if session was validated through proper login flow
    const sessionValidated = sessionStorage.getItem("sessionValidated") === "true" || store.sessionValidated;

    // If trying to access protected route
    if (to.matched.some((record) => record.meta.requiresAuth)) {
      // If no session validation flag, redirect to landing for proper login
      if (!sessionValidated) {
        console.log('Direct URL access detected - no validated session, redirecting to landing page');
        // Clear any stale Cognito session and store data
        try {
          await signOut();
        } catch (signOutError) {
          console.warn('Failed to sign out during validation redirect:', signOutError);
        }
        store.clearsession();
        return next("/");
      }

      // If session validated but no store data, redirect to landing
      if (!hasStoreData) {
        console.log('Session validated but no store data - redirecting to landing page');
        store.clearsession();
        return next("/");
      }

      // If not authenticated via Cognito but trying to access protected route, redirect
      if (!isAuthenticated) {
        console.log('Not authenticated via Cognito - redirecting to landing page');
        store.clearsession();
        return next("/");
      }

      // All checks passed - allow access
      return next();
    }

    // If user is authenticated and store has data, set it in session if needed
    if (isAuthenticated && !store.getuserData) {
      await store.setUserInSession();
    }

    // If trying to access landing/auth pages while already authenticated
    if (
      to.matched.some((record) => record.meta.redirectIfAuth) &&
      isAuthenticated &&
      hasStoreData &&
      sessionValidated
    ) {
      return next("/dashboard");
    }

    next();
  } catch (error) {
    // Reset body overflow on error as well
    document.body.style.overflow = "";
    console.error('Router guard error:', error);

    if (to.matched.some((record) => record.meta.requiresAuth)) {
      return next("/");
    } else {
      return next();
    }
  }
});

export default router;
